import { upsertWorkOrder } from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";
import {
	ErrorModal,
	getMomentTz,
	AssetTreePicker,
	useToken,
	HelmetDexteel,
} from "@dexteel/mesf-core";
import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	DialogTitle,
	TextField,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Grid from "@material-ui/core/Grid";
import Snackbar from "@material-ui/core/Snackbar";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";

import { type SyntheticEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useWorkOrderModalsContext } from "../context/work-order-modals.context";
import { INITIAL_VALUES_MODAL } from "../models/ModalInitialValue";
import type { ModalWorkOrder } from "../models/ModalWorkOrder";
import { CraftPicker } from "./picker/craftPicker";
import type { WorkOrder } from "@/pages/work-orders/models/WorkOrder";
import { useGetAssetPath } from "@/shared/hooks/useGetAssetFullPath";
import { TableWorkOrdesResoures } from "./work-order-resources/table-work-ordes-resoures";
import { SimpleTextAreaControl } from "@/components/controls/SimpleSelectorControl";

const useStyles = makeStyles((theme) => ({
	root: {
		"& .input-group-text": {
			display: "none",
		},
		"& .MuiDialogContent-dividers": {
			padding: 0,
		},
	},
	treePickerIcon: {
		"& .MuiInputAdornment-root": {
			height: "57px",
			color: "#9E9E9E",
			position: "absolute",
			right: 10,
		},
	},
	modalTitle: {
		backgroundColor: "#ced2cc",
		color: "#495057",
		textAlign: "center",
	},
	modalWarning: {
		color: "white",
		backgroundColor: "#dc3545",
		textAlign: "center",
	},
	messageTitle: {
		fontSize: 15,
		fontWeight: "bold",
		color: "#0000008A",
	},
	inputEditable: {
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			border: "2px solid #3F51B5",
		},
	},
	comments: {
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			border: "1px solid #3F51B5",
		},
	},
}));

const moment = getMomentTz();

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	data: ModalWorkOrder | null;
	initialNewMode?: boolean;
	suffixTitle?: string;
};

export function EditWorkOrderModal({
	show,
	onHide,
	data,
	initialNewMode = false,
	suffixTitle,
}: Props) {
	const { getUserName } = useToken();
	const classes = useStyles();

	const {
		state: {
			modalWorkOrderSelected: {
				Comments,
				TemplateTitle,
				WorkOrderOpenedBy,
				WorkOrderName,
				AssetName,
				AssetId,
				WorkOrderId,
			},
		},
		state,
		actions: {
			setComments,
			setWorkOrderNumberOfWorkers,
			setTemplateTitle,
			setWorkOrderPlannedHours,
			setWorkOrderOpenedBy,
			setOpenTimestamp,
			setWorkOrderName,
			setTitle,
			setAssetName,
			setWorkOrderId,
			setDownHours,
			setAttachmentsLinked,
			setCraftId,
			setAssetId,
			setWorkOrderStatusCode,
			setTypeCode,
			setWorkOrderAttachmentsList,
			setWorkOrderDrawingsList,
			setWorkOrderLOTOList,
			setWorkOrderPartsList,
		},
	} = useWorkOrderModalsContext();

	const [saveSuccess, setSaveSuccess] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string>("");
	const [deleteByIdError, setDeleteByIdError] = useState<string>("");
	const [isLoadingAssetPath, setIsLoadingAssetPath] = useState<boolean>(true);
	const [errorAssetPath, setErrorAssetPath] = useState<string>("");

	const { GetAssetFullPath } = useGetAssetPath({
		setIsLoadingAssetPath,
		setErrorAssetPath,
		setAssetName,
	});

	const {
		control,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
		reset,
	} = useForm<ModalWorkOrder>({ defaultValues: INITIAL_VALUES_MODAL });

	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("md");
	const [userInput, setUserInput] = useState<string>("");
	const [openWorkOrdersResources, setOpenWorkOrdersResources] = useState(false);
	const [newMode, setNewMode] = useState(initialNewMode);
	const [successMessage, setSuccessMessage] = useState("");
	const [assetChanged, setAssetChanged] = useState(false);

	const changeSearchAsset = (value: number | null) => {
		if (value !== null) {
			setValue("AssetId", value);
			setAssetId(value);
			GetAssetFullPath(value.toString());
			setAssetChanged(true);
			setWorkOrderAttachmentsList([]);
			setWorkOrderDrawingsList([]);
			setWorkOrderLOTOList([]);
			setWorkOrderPartsList([]);
		}
	};

	const appendComment = (newComment: string) => {
		const now = moment(new Date()).format("MM/DD/YYYY HH:mm");
		let newValue = "";

		if (
			Comments === "" ||
			Comments === null ||
			Comments === "null" ||
			Comments === "undefined" ||
			Comments === undefined
		) {
			newValue = getUserName() + " - " + now + " - " + newComment;
		} else {
			newValue =
				Comments + "\n" + getUserName() + " - " + now + " - " + newComment;
		}

		if (newComment != "") {
			setUserInput("");
			setComments(newValue);
		}
	};

	const addNewComment = () => {
		appendComment(userInput);
	};

	const onKeyDown = (e: any) => {
		if (e.key === "Enter") {
			appendComment(e.target.value);
			e.preventDefault();
		}
	};

	const handleTitleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			e.stopPropagation();
		}
	};

	const handleClose = (event?: SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setSaveSuccess(false);
	};

	const handleCancel = async () => {
		setUserInput("");
		reset();
		onHide(false);
	};

	const changeComments = (value: string) => {
		setComments(value);
	};

	const title = watch("Title");
	const changeTitle = () => {
		setTitle(title);
	};

	const downHours = watch("DownHours");
	const changeDownHours = () => {
		setDownHours(downHours);
	};

	const workOrderNumberOfWorkers = watch("WorkOrderNumberOfWorkers");
	const changeWorkOrderNumberOfWorkers = () => {
		setWorkOrderNumberOfWorkers(workOrderNumberOfWorkers);
	};

	const workOrderPlannedHours = watch("WorkOrderPlannedHours");
	const changeWorkOrderPlannedHours = () => {
		setWorkOrderPlannedHours(workOrderPlannedHours);
	};

	const craftId = watch("CraftId");
	const changeCraftId = () => {
		setCraftId(craftId);
	};
	const assetId = watch("AssetId");
	const changeAssetId = () => {
		setAssetId(assetId);
	};

	const handleWorkOrderResourcesDialog = () => {
		setOpenWorkOrdersResources(true);
	};

	const onSubmit = async () => {
		setIsSubmitLoading(true);
		const res = await upsertWorkOrder(
			state.modalWorkOrderSelected as WorkOrder,
		);
		if (res.ok) {
			setAssetChanged(false);
			setSaveSuccess(true);
			setIsSubmitLoading(false);
			setSuccessMessage(
				newMode
					? "Work order created successfully"
					: "Work order edited successfully",
			);
			if (newMode) {
				setNewMode(false);
			}
			onHide(true);
		} else {
			setIsSubmitLoading(false);
			setError(res.message);
			onHide(false);
		}
	};

	useEffect(() => {
		if (data != null && show) {
			setAssetId(data?.AssetId);
			setWorkOrderStatusCode(data?.WorkOrderStatusCode);
			setComments(data?.Comments);
			setTemplateTitle(data?.TemplateTitle);
			setWorkOrderPlannedHours(data?.WorkOrderPlannedHours);
			setWorkOrderNumberOfWorkers(data?.WorkOrderNumberOfWorkers);
			setWorkOrderOpenedBy(data?.WorkOrderOpenedBy);
			setOpenTimestamp(data?.OpenTimestamp);
			setWorkOrderName(data?.WorkOrderName);
			setWorkOrderId(data?.WorkOrderId);
			setTitle(data?.Title);
			setDownHours(data?.DownHours);
			setCraftId(data?.CraftId);
			setTypeCode(data?.TypeCode);
			setAttachmentsLinked(data?.AttachmentsLinked);
			GetAssetFullPath((data?.AssetId as number)?.toString());
			setCraftId(data?.CraftId);
			setTypeCode(data?.TypeCode);
			reset(data);
			setWorkOrderAttachmentsList([]);
			setWorkOrderDrawingsList([]);
			setWorkOrderLOTOList([]);
			setWorkOrderPartsList([]);
			setUserInput("");
			setIsSubmitLoading(false);
			setNewMode(initialNewMode);
		}
	}, [show, initialNewMode]);

	useEffect(() => {
		changeTitle();
	}, [title]);
	useEffect(() => {
		changeDownHours();
	}, [downHours]);
	useEffect(() => {
		changeCraftId();
	}, [craftId]);
	useEffect(() => {
		changeWorkOrderNumberOfWorkers();
	}, [workOrderNumberOfWorkers]);
	useEffect(() => {
		changeWorkOrderPlannedHours();
	}, [workOrderPlannedHours]);
	useEffect(() => {
		changeAssetId();
	}, [assetId]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`${newMode ? "New Work Order" : "Edit Work Order"}${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<Dialog
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={show}
				onClose={() => handleCancel()}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Grid container style={{ padding: "15px 0 20px" }}>
						<DialogTitle
							style={{
								padding: "0px 40px 10px 24px",
								marginRight: 20,
								width: "100%",
							}}
						>
							<Grid
								item
								md={12}
								xs={12}
								style={{ fontSize: "1.7em", fontWeight: "bold" }}
							>
								{WorkOrderName ? `WORK ORDER: ${WorkOrderName}` : "WORK ORDER"}
							</Grid>
							<Grid item md={12} xs={12}>
								{TemplateTitle && `${data?.TemplateTitle}`}
							</Grid>
						</DialogTitle>
						<Grid container item>
							<DialogContent dividers>
								<Grid container>
									<Grid
										item
										md={12}
										xs={12}
										style={{ padding: 0, marginTop: "-5px" }}
									>
										<Controller
											name="Title"
											control={control}
											rules={{
												validate: (value) => value !== "",
											}}
											render={({ field: { onChange, value } }) => (
												<TextField
													label="Title"
													variant="outlined"
													fullWidth
													error={!!errors.Title}
													margin="dense"
													onKeyDown={handleTitleKeyDown}
													autoComplete="off"
													value={value}
													onChange={(e) => onChange(e)}
													className={classes.inputEditable}
												/>
											)}
										/>
									</Grid>
									<Grid container spacing={2} style={{ paddingBottom: 5 }}>
										<Grid item md={6} xs={12}>
											<Controller
												name="AssetId"
												control={control}
												render={({ field: { value } }) => {
													return (
														<AssetTreePicker
															label="Asset"
															value={value || 0}
															onChange={changeSearchAsset}
														/>
													);
												}}
											/>
										</Grid>

										<Grid item md={6} xs={12}>
											<TextField
												label="Opened by"
												variant="outlined"
												disabled
												value={WorkOrderOpenedBy}
												fullWidth
												margin="dense"
												autoComplete="off"
											/>
										</Grid>
									</Grid>
									<Grid container spacing={4} style={{ marginTop: "-20px" }}>
										<Grid item md={2} xs={2} style={{ paddingRight: 0 }}>
											<Controller
												name="WorkOrderPlannedHours"
												control={control}
												rules={{
													pattern: {
														value: /^-?\d*(.\d+)?$/,
														message: "Enter a valid number",
													},
												}}
												render={({ field: { onChange, value } }) => (
													<TextField
														label="Planned Hours"
														variant="outlined"
														error={!!errors.WorkOrderPlannedHours}
														fullWidth
														margin="dense"
														autoComplete="off"
														value={value ?? ""}
														onChange={(e) => onChange(e)}
														className={classes.inputEditable}
													/>
												)}
											/>
										</Grid>
										<Grid item md={2} xs={2}>
											<Controller
												name="WorkOrderNumberOfWorkers"
												control={control}
												render={({ field: { onChange, value } }) => (
													<TextField
														type="number"
														label="Workers"
														variant="outlined"
														error={!!errors.WorkOrderNumberOfWorkers}
														fullWidth
														margin="dense"
														autoComplete="off"
														value={value ?? ""}
														onChange={(e) => onChange(e)}
														className={classes.inputEditable}
													/>
												)}
											/>
										</Grid>
										<Grid item md={2} xs={2} style={{ paddingLeft: 0 }}>
											<Controller
												name="DownHours"
												control={control}
												render={({ field: { onChange, value } }) => (
													<TextField
														label="Down Hours"
														variant="outlined"
														error={!!errors.DownHours}
														fullWidth
														margin="dense"
														autoComplete="off"
														value={value ?? ""}
														onChange={(e) => onChange(e)}
														className={classes.inputEditable}
													/>
												)}
											/>
										</Grid>
										<Grid item md={3} xs={6} style={{ paddingLeft: 0 }}>
											<Controller
												name="CraftId"
												control={control}
												render={({ field: { onChange, value } }) => (
													<CraftPicker
														value={value as number}
														onChange={onChange}
													/>
												)}
											/>
										</Grid>
										<Grid
											item
											md={3}
											xs={6}
											style={{ paddingLeft: 0, paddingRight: 15 }}
										>
											<TextField
												label="Opened"
												variant="outlined"
												disabled
												value={moment(data?.OpenTimestamp).format(
													"MM/DD/YYYY HH:mm A",
												)}
												fullWidth
												margin="dense"
												autoComplete="off"
											/>
										</Grid>
									</Grid>
								</Grid>
								<Grid container justifyContent="flex-end">
									<Grid
										item
										md={12}
										xs={12}
										style={{
											marginBottom: "10px",
											marginTop: "22px",
											padding: 0,
										}}
									>
										<Card
											variant="outlined"
											style={{ backgroundColor: "#E9F2F8" }}
											className={classes.root}
										>
											<CardContent>
												<Typography className={classes.messageTitle}>
													Comments:
												</Typography>
												<SimpleTextAreaControl
													value={Comments || ""}
													onChange={changeComments}
													disabled
													showColor={false}
													styleText={{
														height: "80px",
														border: "none",
														backgroundColor: "#E9F2F8",
														fontSize: 16,
													}}
												/>
											</CardContent>
										</Card>
									</Grid>
									<Grid
										container
										spacing={2}
										justifyContent="center"
										alignItems="center"
									>
										<Grid item md={9} xs={12}>
											<TextField
												variant="outlined"
												margin="dense"
												multiline
												maxRows={3}
												value={userInput}
												onChange={(e) => setUserInput(e.target.value)}
												label="Comment"
												fullWidth
												onKeyDown={onKeyDown}
												autoComplete="off"
												className={classes.comments}
											/>
										</Grid>
										<Grid item md={3} xs={12}>
											<Button
												fullWidth
												variant="contained"
												color="primary"
												onClick={addNewComment}
												style={{ height: "40px", marginTop: 2 }}
											>
												Add comment
											</Button>
										</Grid>
									</Grid>
								</Grid>
							</DialogContent>
							<Grid
								item
								md={12}
								xs={12}
								justifyContent="flex-end"
								style={{ marginTop: "15px" }}
							>
								<DialogActions style={{ padding: "0px 24px" }}>
									<Grid container>
										<Grid
											item
											md={4}
											xs={4}
											spacing={2}
											style={{ display: "flex", alignItems: "center" }}
											title={
												assetChanged
													? "You need to save to be able to manage resources"
													: ""
											}
										>
											{newMode == false && (
												<Button
													fullWidth
													variant="contained"
													color="primary"
													disabled={assetChanged}
													onClick={handleWorkOrderResourcesDialog}
													title={
														assetChanged
															? "You need to save to be able to manage resources"
															: ""
													}
												>
													MANAGE RESOURCES
												</Button>
											)}
										</Grid>
										<Grid
											item
											md={8}
											xs={8}
											container
											justifyContent="flex-end"
											spacing={2}
											style={{ margin: 0 }}
										>
											<Grid item md={4} xs={4}>
												<Button
													fullWidth
													variant="contained"
													color="default"
													onClick={handleCancel}
												>
													CANCEL
												</Button>
											</Grid>
											<Grid item md={4} xs={4} style={{ paddingRight: 0 }}>
												<Button
													fullWidth
													startIcon={
														isSubmitLoading && <CircularProgress size="1rem" />
													}
													disabled={isSubmitLoading}
													variant="contained"
													color="primary"
													type="submit"
												>
													SAVE
												</Button>
											</Grid>
										</Grid>
									</Grid>
								</DialogActions>
							</Grid>
						</Grid>
					</Grid>
				</form>
			</Dialog>
			<TableWorkOrdesResoures
				show={openWorkOrdersResources}
				onHide={(shouldUpdate: boolean) => {
					setOpenWorkOrdersResources(false);
				}}
				AssetId={AssetId}
				WorkOrderId={WorkOrderId}
				WorkOrderName={data?.WorkOrderName as string}
				Title={data?.Title as string}
				AssetName={(data?.AssetName as string)?.replace(/.*\/(.+)$/, "$1")}
				TypeCode={data?.TypeCode as string}
				CraftId={data?.CraftId as number}
				OpenTimestamp={data?.OpenTimestamp as Date}
				CloseTimestamp={data?.CloseTimestamp as Date}
			/>
			<Snackbar
				open={saveSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert severity="info" onClose={handleClose}>
					{successMessage}
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={deleteByIdError}
				onHide={() => setDeleteByIdError("")}
			/>
			<ErrorModal error={errorAssetPath} onHide={() => setErrorAssetPath("")} />
		</>
	);
}
